<template>
  <v-sheet>
    <v-container class="footer-offset pa-0 mb-16" ref="top" id="guidance-calculator-tab">
      <v-row no-gutters class="mb-16">
        <!-- class="bottom-left" -->
        <v-col cols="12" md="8" lg="8" class="guidance-panel">
          <!-- Life Guidance Hero -->
          <v-row class="mx-0">
            <v-col cols="12" class="header">
              <h3
                role="presentation"
                class="text-h1 pt-4 mt-3 mr-4 font-weight-bold text-uppercase text--white text-left maxwidth-hero"
              >
                {{ $t('L_life_guidance_calculator') }}
              </h3>
            </v-col>
          </v-row>
          <!-- Rule of Thumb -->
          <div class="mx-0 mb-4 pa-4 rule-of-thumb d-flex align-center ga-4">
            <v-img src="./assets/md-rule.svg" width="48" max-width="48" min-width="48" alt="rule" />
            <div>
              <h3 role="heading" aria-level="2">
                {{ $t('L_rule_of_thumb') }}
              </h3>
              <p class="text-body-1">
                {{ $t('L_rule_subheading') }}
              </p>
            </div>
          </div>
          <!-- Guidance Text -->
          <v-row class="mx-6">
            <v-col class="px-0">
              <h4 class="text-h4 mb-4 font-weight-regular">
                {{ $t('L_when_you_die') }}
              </h4>
              <h4 class="text-h4 mb-4 pb-4 font-weight-regular">
                {{ $t('L_use_guidance') }}
              </h4>
              <h2 class="text-h2 mt-4 pt-4 mb-8">
                {{ $t('L_adjust_amounts') }}
              </h2>
            </v-col>
          </v-row>

          <v-expansion-panels role="presentation" :multiple="true" v-model="panel" flat>
            <v-expansion-panel role="presentation" value="immediateCashNeeds" class="lineTopBorder">
              <v-expansion-panel-title class="px-5">
                <div class="d-flex align-center ga-4">
                  <v-img
                    src="@/modules/policy/PolicyGuidanceCalculator/assets/immediate.svg"
                    alt="immediate cash need"
                    role="presentation"
                    width="40"
                  />
                  <h2 id="immediate_cash_need_container" class="text-h2">
                    <span
                      ref="cash"
                      role="button"
                      tabindex="0"
                      :aria-expanded="`${!!panel[0]}`"
                      id="immediate_cash_need_header"
                    >
                      {{ $t('L_immediate_cash_needs') }}
                    </span>
                  </h2>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <ImmediateCashNeeds
                  v-if="!loading"
                  :dime-data="immediateCashNeed"
                  @updated="onCashUpdate"
                  @next="nextPanel"
                  @include-spouse="onSpouseIncluded"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
            <!-- </v-expansion-panels>
          <br>
          <v-expansion-panels flat> -->
            <v-expansion-panel role="presentation" value="incomeReplacement" class="lineTopBorder">
              <v-expansion-panel-title class="px-5">
                <div class="d-flex align-center ga-4">
                  <v-img src="./assets/income.svg" alt="" role="presentation" width="40" />
                  <h2 id="income_replacement_container" class="text-h2">
                    <span
                      ref="income"
                      role="button"
                      tabindex="0"
                      :aria-expanded="`${!!panel[1]}`"
                      id="income_replacement_header"
                    >
                      {{ $t('L_income_replacement') }}
                    </span>
                  </h2>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <OngoingExpenses
                  v-if="!loading"
                  :dime-data="incomeReplacement"
                  id="income_replacement_panel"
                  v-model="panels.incomeReplacement"
                  @updated="onIncomeUpdate"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <Sidebar :current-coverage="currentCoverage" v-if="!$vuetify.display.mdAndUp" />
        </v-col>
        <!-- class="bottom-right" -->
        <v-col
          cols="12"
          md="4"
          lg="4"
          class="mx-0 pa-0 bg-grey-lighten-3"
          v-if="$route?.name === 'GuidanceCalculator'"
        >
          <Sidebar :current-coverage="currentCoverage" v-if="$vuetify.display.mdAndUp" />
        </v-col>
        <v-col cols="12" class="ma-2">
          <v-spacer></v-spacer>
          <div
            class="float-right mr-4 hidden-md-and-down circle cursor-pointer"
            @click="scrollToTop"
          >
            <div class="ma-2 text-center" color="primary">
              <v-icon :icon="mdiChevronUp" class="mb-0 text-center" />
              <h6 role="presentation" class="text-h6 text-center font-weight-regular">
                {{ $t('L_back_to_top') }}
              </h6>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import bff from '@/shared/api/bff';
import { mapState, mapStores } from 'pinia';
import Achievement from '@/components/Achievement/Achievement.vue';
import Slider from '@/components/Inputs/Slider/Slider.vue';
import { useDimeguidanceStore } from '@/modules/dimeguidance/store/dimeguidance.store';
import CurrencyDisplay from './components/CurrencyDisplay.vue';
import ImmediateCashNeeds from './components/ImmediateCashNeeds.vue';
import OngoingExpenses from './components/OngoingExpenses.vue';
import GuidancePill from './components/GuidancePill.vue';
import OtherCoverage from './components/OtherCoverage.vue';
import Sidebar from './components/Sidebar.vue';
import { usePolicyStore } from '../store/policy.store';
import { mdiChevronUp } from '@mdi/js';
import { useGoTo } from 'vuetify';
import { OnexButton } from '@primerica/onex-ui-lib';
import type {
  IDimeguidanceDimeData,
  IDimeguidanceDimeResponse,
} from '@/modules/dimeguidance/store/dimeguidanceStore.model.ts';

const componentOptions: ComponentOptions = {
  name: 'GuidanceCalculatorTab',
  setup() {
    const goTo = useGoTo();
    return { goTo };
  },
  components: {
    Sidebar,
    OtherCoverage,
    GuidancePill,
    OngoingExpenses,
    ImmediateCashNeeds,
    CurrencyDisplay,
    Slider,
    Achievement,
    OnexButton,
  },
  data: () => ({
    amount: 0,
    loading: false,
    panel: ['incomeReplacement', 'immediateCashNeeds'],
    incomeReplacement: {
      you: {
        need: 0,
        years: 15,
      },
      spouse: {
        need: 0,
        years: 15,
      },
    },
    spouseOngoingExpenses: 0,
    immediateCashNeed: {
      you: {
        mortgage: 0,
        debt: 0,
        education: 0,
        funeral: 8000,
      },
      spouse: {
        mortgage: 0,
        debt: 0,
        education: 0,
        funeral: 8000,
      },
    } as IDimeguidanceDimeData,
    vertical: true,
    panels: {
      immediateCashNeeds: true,
      incomeReplacement: false,
    },
    debounce: undefined,
    yourCalculationDebounce: undefined,
    spouseCalculationDebounce: undefined,
    spouseCalculationIncluded: false,
    scrollListener: undefined,
    showScrollButton: false,
    mdiChevronUp,
  }),
  validations: {
    amount: {},
  },
  computed: {
    ...mapStores(usePolicyStore, useDimeguidanceStore),
    ...mapState(useDimeguidanceStore, {
      dimeData: 'dimeData',
      includeSpouse: 'includeSpouse',
    }),
    ...mapState(usePolicyStore, {
      activePrimaryCoverage: 'activePrimaryCoverage',
      activeSpouseCoverage: 'activeSpouseCoverage',
    }),
    currentCoverage() {
      return {
        you: this.activePrimaryCoverage || 0,
        spouse: this.activeSpouseCoverage || 0,
      };
    },
  },
  methods: {
    addViewDetails() {
      if (!document.querySelector('#app > #footer_wrapper')) {
        const tabletFooter: HTMLElement | null = document.getElementById('tablet_footer');
        const mobileFooter: HTMLElement | null = document.getElementById('mobile_footer');
        const appWrapper: HTMLElement | null = document.getElementById('app');
        const footerWrapper: HTMLDivElement = document.createElement('div');
        footerWrapper.id = 'footer_wrapper';
        footerWrapper.classList.add('gray', 'lighten-3', 'bottom-right', 'hidden-lg-and-up');
        footerWrapper.style.backgroundColor = 'rgb(238,238,238)';
        footerWrapper.style.position = 'fixed';
        footerWrapper.style.bottom = '110px';
        footerWrapper.style.right = '28px';
        footerWrapper.style.left = '28px';
        if (tabletFooter) {
          footerWrapper.append(tabletFooter);
        }
        if (mobileFooter) {
          footerWrapper.append(mobileFooter);
        }
        if (appWrapper) appWrapper.append(footerWrapper);
      }
    },
    removeViewDetails() {
      const footer = document.querySelector('#app > #footer_wrapper');
      if (footer) {
        footer.remove();
      }
      window.removeEventListener('scroll', this.scrollListener);
    },
    backToTop(e: MouseEvent) {
      if (e.target) {
        (e.target as HTMLElement).click();
      }
    },
    initialize(data: IDimeguidanceDimeResponse | null) {
      this.immediateCashNeed.you.mortgage = data?.primary?.mortgage ?? 0;
      this.immediateCashNeed.you.debt = data?.primary?.debt ?? 0;
      this.immediateCashNeed.you.education = data?.primary?.education ?? 0;
      this.immediateCashNeed.you.funeral = data?.primary?.funeral ?? 0;
      this.incomeReplacement.you.need = data?.primary?.incomeGoal ?? 0;
      this.incomeReplacement.you.years = data?.primary?.yearsOfNeed ?? 0;
      this.incomeReplacement.spouse.need = data?.spouse?.incomeGoal ?? 0;
      this.incomeReplacement.spouse.years = data?.spouse?.yearsOfNeed ?? 0;
      this.spouseCalculationIncluded = data?.primary?.spouseIncluded === 'Y';
      if (this.spouseCalculationIncluded) {
        this.immediateCashNeed.spouse.mortgage = data?.spouse?.mortgage ?? 0;
        this.immediateCashNeed.spouse.debt = data?.spouse?.debt ?? 0;
        this.immediateCashNeed.spouse.education = data?.spouse?.education ?? 0;
        this.immediateCashNeed.spouse.funeral = data?.spouse?.funeral ?? 0;
        this.incomeReplacement.spouse.need = data?.spouse?.incomeGoal ?? 0;
        this.incomeReplacement.spouse.years = data?.spouse?.yearsOfNeed ?? 0;
        this.calculateSpouseDime();
      }
      this.calculateYourDime();
      this.loading = false;
    },
    onSpouseIncluded(spouseIncluded: boolean) {
      if (spouseIncluded && !this.spouseCalculationIncluded) {
        this.immediateCashNeed.spouse = Object.assign({}, this.immediateCashNeed.you);
        this.immediateCashNeed = Object.assign({}, this.immediateCashNeed);
        this.spouseCalculationIncluded = true;
        this.calculateSpouseDime();
      }
      this.dimeguidanceStore.setIncludeSpouse(spouseIncluded);
      this.saveDimeData();
    },
    scrollToIncomeReplacement() {
      // see https://vuetifyjs.com/en/features/scrolling/#programmatic-scrolling
      this.goTo(0, { container: '#income_replacement_container' });
    },
    nextPanel(): void {
      window.BrowserAgentExtension?.logTextMetric?.({
        key: 'Click',
        value: 'Guidance.Calculator.Next',
      });
      this.panel = [false, true];
      window.setTimeout(() => this.scrollToIncomeReplacement(), 300);
    },
    onCashUpdate(event: IDimeguidanceDimeData) {
      this.immediateCashNeed = event;
      this.saveDimeData();
      this.calculateYourDime();
      this.calculateSpouseDime();
    },
    onIncomeUpdate(event: IDimeguidanceDimeData) {
      this.incomeReplacement = event;
      this.saveDimeData();
      this.calculateYourDime();
      this.calculateSpouseDime();
    },
    saveCallback(data: Record<string, unknown>) {
      return async () => {
        await bff.saveDimeData(data);
      };
    },
    calculateYouCallback() {
      this.dimeguidanceStore.calculateYou({
        incomeNeed: this.incomeReplacement?.you?.need,
        yearsOfNeed: this.incomeReplacement?.you?.years,
        mortgage: this.immediateCashNeed?.you?.mortgage,
        debt: this.immediateCashNeed?.you?.debt,
        education: this.immediateCashNeed?.you?.education,
        funeral: this.immediateCashNeed?.you?.funeral,
        spouseIncluded: this.includeSpouse,
      });
    },
    calculateSpouseCallback() {
      this.dimeguidanceStore.calculateSpouse({
        incomeNeed: this.incomeReplacement.spouse.need,
        yearsOfNeed: this.incomeReplacement.spouse.years,
        mortgage: this.immediateCashNeed.spouse.mortgage,
        debt: this.immediateCashNeed.spouse.debt,
        education: this.immediateCashNeed.spouse.education,
        funeral: this.immediateCashNeed.spouse.funeral,
        spouseIncluded: this.spouseCalculationIncluded ? true : this.includeSpouse,
      });
    },
    calculateYourDime() {
      if (typeof this.yourCalculationDebounce !== 'undefined') {
        window.clearTimeout(this.yourCalculationDebounce);
      }
      this.yourCalculationDebounce = window.setTimeout(() => this.calculateYouCallback(), 1000);
    },
    calculateSpouseDime() {
      if (typeof this.spouseCalculationDebounce !== 'undefined') {
        window.clearTimeout(this.spouseCalculationDebounce);
      }
      this.spouseCalculationDebounce = window.setTimeout(
        () => this.calculateSpouseCallback(),
        1000
      );
    },
    saveDimeData() {
      if (typeof this.debounce !== 'undefined') {
        window.clearTimeout(this.debounce);
      }
      const dimeData = {
        primary: {
          currentCoverage: this.currentCoverage.you,
          ...this.immediateCashNeed.you,
          ...this.incomeReplacement.you,
          spouseIncluded: this.includeSpouse,
        },
        spouse: {
          currentCoverage: this.currentCoverage.spouse,
          ...this.immediateCashNeed.spouse,
          ...this.incomeReplacement.spouse,
          spouseIncluded: this.includeSpouse,
        },
      };
      this.debounce = window.setTimeout(async () => await this.saveCallback(dimeData)(), 2000);
    },
    onScroll() {
      const scrollPosition = document.body.scrollTop;
      const windowHeight = document.body.clientHeight;
      const documentHeight = document.body.offsetHeight;
      this.showScrollButton = documentHeight - scrollPosition < windowHeight + 50;
    },
    scrollToTop() {
      // see https://vuetifyjs.com/en/features/scrolling/#programmatic-scrolling
      window.scrollTo(0, 0);
    },
  },
  watch: {
    $route(to, from) {
      const fromPath = from.name;
      const toPath = to.name;
      if (fromPath === 'GuidanceCalculator' && toPath !== 'GuidanceCalculator') {
        // this.removeViewDetails();
      } else if (fromPath !== 'GuidanceCalculator' && toPath === 'GuidanceCalculator') {
        this.addViewDetails();
      }
    },
  },
  mounted() {
    this.scrollListener = window.addEventListener('scroll', this.onScroll);
    this.loading = true;
    this.dimeguidanceStore.getData().then(this.initialize);
    this.addViewDetails();
  },
  async unmounted() {
    await this.removeViewDetails();
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.lineTopBorder {
  border-top: 1px solid #cccccc;
}
.card {
  border-radius: 0 0 3px 3px;
}

.left-panel {
  border-radius: 0 0 0 3px;
}

.bottom {
  border-radius: 0;
  &-right {
    border-bottom-right-radius: 3px;
    @media only screen and (max-width: 768px) {
      border-bottom-right-radius: 3px;
      left: 3px;
    }
  }
  &-left {
    border-bottom-left-radius: 3px;
    @media only screen and (max-width: 768px) {
      width: 100% !important;
      border-radius: 0;
    }
  }
}

@media only screen and (max-width: 1023px) and (min-width: 768px) {
  .bottom-right {
    display: none;
  }
}

.footer-offset {
  @media only screen and (max-width: 1023px) {
    margin-bottom: 110px;
  }
  @media only screen and (max-width: 500px) {
    margin-bottom: 110px;
  }
}

.text--white {
  color: white;
  font-size: 1.52em !important;
  line-height: 1.4em;
  font-size: 26px;
  max-width: 300px;
  padding-left: 20px;
  @media only screen and (max-width: 870px) {
    max-width: 190px;
    font-size: 1.1em !important;
  }
  @media only screen and (max-width: 500px) {
    max-width: 190px;
    font-size: 0.8em !important;
  }
}

.header {
  height: 144px;
  background-image: url(assets/family2.png);
  background-position: center;
  background-size: cover;
  position: relative;
  @media only screen and (max-width: 500px) {
    height: 89px !important;
  }
}

.rule-of-thumb {
  position: relative;
  background-color: #f3f3f3;
}

.scroll-to-top {
  position: absolute;
  bottom: 10px;
  right: 10px;
  height: 76px;
  width: 76px;
  opacity: 0;
  transition: opacity 300ms;
  &.show {
    opacity: 1;
  }
  background-color: #02579e;
  border-radius: 50%;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3) !important;
  h6.heading_six {
    margin-top: -6px;
    color: #fff;
  }
  & > i {
    line-height: 0.6em;
    color: #fff !important;
    font-size: 4em !important;
  }
}

:deep(input) {
  &#mortgage_display,
  &#years_display {
    text-align: center !important;
  }
}

:deep(.slider-tooltip) {
  margin-top: -42px;
}

:deep(.v-btn--small) {
  text-transform: none;
  margin: 0 0 0 8px;
}

:deep(img.heading-icon) {
  margin: 0 16px -10px 0;
  @media only screen and (max-width: 399px) {
    height: 1.8em;
    margin-right: 10px;
  }
}

:deep(.v-text-field.text-xs-center) {
  input {
    text-align: center !important;
  }
}

.v-expansion-panels {
  box-shadow: none;
}
:deep(.v-expansion-panel__header) {
  border-top: 1px solid #cccccc;
  padding-top: 32px;
  padding-bottom: 32px;
  .v-expansion-panel__header__icon {
    i {
      font-size: 2em !important;
    }
  }
  @media only screen and (max-width: 399px) {
    padding: 16px;
    h2.heading_two {
      font-size: 18px;
    }
  }
}

:deep(.expansion-panel-header) {
  @media only screen and (max-width: 399px) {
    padding: 16px !important;
    h2.heading_two {
      font-size: 18px !important;
    }
  }
}

:deep(.v-expansion-panel-text__wrapper) {
  padding-left: unset;
  padding-right: unset;
}

.v-expansion-panel__container {
  border-top: none !important;
}
</style>
<style lang="scss">
#guidance-calculator-tab {
  .v-text-field__suffix {
    padding-top: 0;
  }
}
.circle {
  width: 90px;
  height: 90px;
  background-color: #02579e;
  color: white;
  font-weight: 600;
  border-radius: 50%;
}
</style>
